import type React from "react";

type PieSkeletonProps = {
	sliceCount?: number;
	showLegend?: boolean;
	legendItems?: number;
	legendSide?: "right" | "left";
	legendWidthPct?: number; // % of wrapper width
};

export const PieSkeleton: React.FC<PieSkeletonProps> = ({ sliceCount = 5, showLegend = false, legendItems = 4, legendSide = "right", legendWidthPct = 36 }) => {
	// --- pie slices (same as before) ---
	const totalAngle = 360;
	const sliceAngles = Array.from({ length: sliceCount }, () => 30 + Math.random() * 60);
	const total = sliceAngles.reduce((a, b) => a + b, 0);
	const scale = totalAngle / total;

	let startAngle = 0;
	const slices = sliceAngles.map((angle, i) => {
		const scaled = angle * scale;
		const endAngle = startAngle + scaled;
		const largeArc = scaled > 180 ? 1 : 0;
		const radius = 35,
			innerRadius = 20;

		const start = polarToCartesian(50, 50, radius, endAngle);
		const end = polarToCartesian(50, 50, radius, startAngle);
		const innerStart = polarToCartesian(50, 50, innerRadius, endAngle);
		const innerEnd = polarToCartesian(50, 50, innerRadius, startAngle);

		const d = `M ${start.x} ${start.y}
      A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}
      L ${innerEnd.x} ${innerEnd.y}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 1 ${innerStart.x} ${innerStart.y}
      Z`;

		startAngle += scaled;

		const lightness = 85 + Math.random() * 10; // 85–95%
		return (
			<path key={i} d={d} fill={`hsl(0, 0%, ${lightness}%)`} style={{ animation: "fadePulse 1.6s ease-in-out infinite", animationDelay: `${i * 0.1}s` }} />
		);
	});

	// --- legend skeleton ---
	const legend = showLegend ? (
		<div
			style={{
				width: `${legendWidthPct}%`,
				minWidth: 120,
				display: "flex",
				flexDirection: "column",
				gap: 12,
				paddingInline: 8,
			}}
		>
			{Array.from({ length: legendItems }).map((_, i) => {
				const l = 70 + Math.random() * 20; // label bar lightness
				const p = 75 + Math.random() * 10; // percent pill lightness
				return (
					<div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<span
							style={{
								width: 12,
								height: 12,
								borderRadius: "50%",
								background: `hsl(0, 0%, ${80 + Math.random() * 10}%)`,
								animation: "fadePulse 1.6s ease-in-out infinite",
								animationDelay: `${i * 0.05}s`,
								flex: "0 0 12px",
							}}
						/>
						<span
							style={{
								height: 10,
								width: `${48 + Math.random() * 32}%`,
								borderRadius: 6,
								background: `hsl(0, 0%, ${l}%)`,
								animation: "fadePulse 1.6s ease-in-out infinite",
								animationDelay: `${i * 0.05 + 0.1}s`,
								flex: "1 1 auto",
							}}
						/>
						<span
							style={{
								height: 10,
								width: 32,
								borderRadius: 6,
								background: `hsl(0, 0%, ${p}%)`,
								animation: "fadePulse 1.6s ease-in-out infinite",
								animationDelay: `${i * 0.05 + 0.2}s`,
								flex: "0 0 32px",
							}}
						/>
					</div>
				);
			})}
		</div>
	) : null;

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: legendSide === "right" ? "row" : "row-reverse",
				alignItems: "center",
				justifyContent: "center",
				gap: 12,
			}}
		>
			{/* pie area keeps square aspect */}
			<div style={{ flex: `0 0 calc(${100 - (showLegend ? legendWidthPct : 0)}%)`, display: "flex", justifyContent: "center", alignItems: "center" }}>
				<svg viewBox="0 0 100 100" width="70%" height="70%">
					{slices}
					<style>{"@keyframes fadePulse { 0%,100%{opacity:.6} 50%{opacity:1} }"}</style>
				</svg>
			</div>
			{legend}
		</div>
	);
};

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
	const rad = ((angleDeg - 90) * Math.PI) / 180;
	return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
